import type { QueryClient } from '@tanstack/react-query';
import type { Application, ApplicationState, BillingStatus } from '@/types';
import type { DeferredApplicationCounts } from '@/stores/pendingBillingStore';
import { shouldClearProcessingIndicator } from '@/lib/bulkProcessQueueSync';
import { createApplicationListSync } from '@/lib/applicationListSync';
import { queryKeys } from '@/lib/queryKeys';
import { hasCandidatesProcessingCredits } from '@/lib/billingCandidatesPool';

/**
 * UI state when the plan has processing capacity but Firestore/list cache
 * still shows PENDING_UPGRADE until unlock-deferred completes.
 */
export function resolveDisplayApplicationState(
  state: ApplicationState,
  hasProcessingCredits?: boolean,
): ApplicationState {
  if (!hasProcessingCredits && state === 'READY_TO_PROCESS') {
    return 'PENDING_UPGRADE';
  }
  if (hasProcessingCredits && state === 'PENDING_UPGRADE') {
    return 'READY_TO_PROCESS';
  }
  return state;
}

export type ApplicationStatusFilterQueue = {
  /** Aligns with `ApplicationRowProcessBadge` spinner — not raw `processingIds`. */
  isRowProcessing?: (id: string) => boolean;
};

/** Before showing "Processing stuck" — cover trigger runtime (300s) + list/detail lag. */
export const PIPELINE_IN_FLIGHT_GRACE_MS = 12 * 60 * 1000;

function parseApplicationTimestampMs(
  value: Application['updatedAt'] | Application['appliedAt'] | undefined,
): number | null {
  if (!value) return null;
  if (typeof value === 'string') {
    const ms = Date.parse(value);
    return Number.isNaN(ms) ? null : ms;
  }
  if (typeof value === 'object' && '_seconds' in value) {
    return value._seconds * 1000 + Math.floor((value._nanoseconds ?? 0) / 1e6);
  }
  return null;
}

/**
 * Automatic screening (apply / manual add) — server is PIPELINE_RUNNING but row is not in bulk UI queue.
 */
export function isApplicationPipelineRecentlyInFlight(
  app: Pick<Application, 'state' | 'updatedAt' | 'appliedAt'>,
  graceMs = PIPELINE_IN_FLIGHT_GRACE_MS,
): boolean {
  if (app.state !== 'PIPELINE_RUNNING') return false;
  const ts =
    parseApplicationTimestampMs(app.updatedAt)
    ?? parseApplicationTimestampMs(app.appliedAt);
  if (ts == null) return true;
  return Date.now() - ts < graceMs;
}

/** Active "Processing" badge / filter — UI queue, bulk batch, or fresh server-side pipeline. */
export function isApplicationPipelineInFlightDisplay(
  app: Pick<Application, 'id' | 'state' | 'updatedAt' | 'appliedAt'>,
  isInUiQueue: boolean,
): boolean {
  if (isInUiQueue) return true;
  return isApplicationPipelineRecentlyInFlight(app);
}

/** Status dropdown / pin — same rules as the status badge. */
export function matchesApplicationStatusFilter(
  app: Pick<Application, 'id' | 'state'>,
  statusFilter: string,
  hasProcessingCredits?: boolean,
  queue?: ApplicationStatusFilterQueue,
): boolean {
  if (statusFilter === 'all') return true;
  if (statusFilter === 'PENDING_UPGRADE') {
    if (!hasProcessingCredits) {
      return app.state === 'PENDING_UPGRADE' || app.state === 'READY_TO_PROCESS';
    }
    return app.state === 'PENDING_UPGRADE';
  }
  if (statusFilter === 'READY_TO_PROCESS') {
    if (queue?.isRowProcessing?.(app.id) === true) return false;
    return isApplicationReadyToProcess(app, hasProcessingCredits);
  }
  const rowProcessing = queue?.isRowProcessing?.(app.id) === true;
  if (statusFilter === 'PIPELINE_RUNNING') {
    return rowProcessing;
  }
  if (statusFilter === 'PIPELINE_STUCK') {
    return isApplicationProcessingStuckDisplay(app, rowProcessing);
  }
  return resolveDisplayApplicationState(app.state, hasProcessingCredits) === statusFilter;
}

/**
 * Billing-blocked — no processing credits left.
 * With credits, deferred rows are processable (see isApplicationReadyToProcess).
 */
export function isApplicationAwaitingUpgrade(
  app: Pick<Application, 'state'>,
  hasProcessingCredits?: boolean,
): boolean {
  if (hasProcessingCredits) return false;
  return app.state === 'PENDING_UPGRADE' || app.state === 'READY_TO_PROCESS';
}

/** Post-upgrade — CV saved, waiting for recruiter to start screening (plan has capacity). */
export function isApplicationReadyToProcess(
  app: Pick<Application, 'state'>,
  hasProcessingCredits?: boolean,
): boolean {
  if (!hasProcessingCredits) return false;
  return (
    app.state === 'READY_TO_PROCESS'
    || app.state === 'PENDING_UPGRADE'
  );
}

/** Legacy billing-blocked pipeline error — treat as awaiting upgrade in UI. */
export function isApplicationBillingPipelineError(
  app: Pick<Application, 'state' | 'lastFailureReason'>,
): boolean {
  return app.state === 'PIPELINE_ERROR' && app.lastFailureReason === 'billing_limit_reached';
}

/** Show only the upgrade/deferred badge (hide primary StatusBadge). */
export function shouldHidePrimaryStatusBadge(
  app: Pick<Application, 'state' | 'lastFailureReason'>,
  hasProcessingCredits?: boolean,
): boolean {
  return (
    isApplicationAwaitingUpgrade(app, hasProcessingCredits)
    || isApplicationReadyToProcess(app, hasProcessingCredits)
    || isApplicationBillingPipelineError(app)
  );
}

/** Non-billing pipeline error — recoverable via process-now retry. */
export function isApplicationPipelineErrorRetry(
  app: Pick<Application, 'state' | 'lastFailureReason'>,
): boolean {
  return app.state === 'PIPELINE_ERROR' && app.lastFailureReason !== 'billing_limit_reached';
}

/** Already screened — re-run initial AI screening (uses AI credits). */
export function isApplicationReprocessable(
  app: Pick<Application, 'state'>,
): boolean {
  return app.state === 'AWAITING_DECISION' || app.state === 'APPLIED_NEW';
}

/**
 * Stuck in-flight — PIPELINE_RUNNING outside the grace window and not covered by UI queue.
 * Used for bulk abort, confirm breakdown, and status filters.
 */
export function isApplicationStuckProcessing(
  app: Pick<Application, 'state' | 'updatedAt' | 'appliedAt'>,
  isInUiQueue = false,
): boolean {
  return isApplicationProcessingStuckDisplay(app, isInUiQueue);
}

/** Row is in the active bulk/UI processing queue (spinner badge). */
export function isApplicationActivelyProcessing(
  app: Pick<Application, 'id' | 'state'>,
  inFlightProcessingIds?: ReadonlySet<string>,
): boolean {
  return (
    inFlightProcessingIds?.has(app.id) === true
    && !shouldClearProcessingIndicator(app.state)
  );
}

/**
 * Display-only: PIPELINE_RUNNING with no UI queue and outside the in-flight grace window.
 */
export function isApplicationProcessingStuckDisplay(
  app: Pick<Application, 'state' | 'updatedAt' | 'appliedAt'>,
  isInUiQueue: boolean,
): boolean {
  return (
    app.state === 'PIPELINE_RUNNING'
    && !isApplicationPipelineInFlightDisplay(app, isInUiQueue)
  );
}

/** Any deferred application that should appear in the upgrade/process banner. */
export function isApplicationBannerDeferred(
  app: Pick<Application, 'state' | 'lastFailureReason'>,
  hasProcessingCredits?: boolean,
): boolean {
  return (
    isApplicationAwaitingUpgrade(app, hasProcessingCredits)
    || isApplicationReadyToProcess(app, hasProcessingCredits)
  );
}

/** Bulk "Processing" action — any state we can start or re-run via process-now. */
export function isApplicationBulkProcessable(
  app: Pick<Application, 'state' | 'lastFailureReason' | 'archived'>,
  hasProcessingCredits?: boolean,
): boolean {
  if (app.archived === true) return false;
  return (
    isApplicationReadyToProcess(app, hasProcessingCredits)
    || isApplicationBillingPipelineError(app)
    || isApplicationPipelineErrorRetry(app)
    || isApplicationReprocessable(app)
    || isApplicationStuckProcessing(app)
  );
}

/** Toolbar "Process all pending (N)" — READY_TO_PROCESS + billing-blocked retries. */
export function isApplicationToolbarProcessable(
  app: Pick<Application, 'state' | 'lastFailureReason'>,
  hasProcessingCredits?: boolean,
): boolean {
  return (
    isApplicationReadyToProcess(app, hasProcessingCredits)
    || isApplicationBillingPipelineError(app)
  );
}

export function getToolbarProcessableApps(
  applications: Application[],
  hasProcessingCredits?: boolean,
): Application[] {
  return applications.filter(
    (a) => a.archived !== true && isApplicationToolbarProcessable(a, hasProcessingCredits),
  );
}

/** All rows matching the "To be processed" status filter (incl. PENDING_UPGRADE when plan has credits). */
export function getReadyToProcessApplications(
  applications: Application[],
  hasProcessingCredits?: boolean,
): Application[] {
  return applications.filter(
    (a) =>
      a.archived !== true
      && matchesApplicationStatusFilter(a, 'READY_TO_PROCESS', hasProcessingCredits),
  );
}

/** Row the recruiter explicitly selected for Stop — server must be PIPELINE_RUNNING. */
export function isApplicationAbortableProcessing(
  app: Pick<Application, 'state' | 'archived'>,
): boolean {
  return app.archived !== true && app.state === 'PIPELINE_RUNNING';
}

/** Stop action — server abort or clear stale UI queue overlay. */
export function isApplicationExplicitStopEligible(
  app: Pick<Application, 'state' | 'archived'>,
  inUiQueue: boolean,
): boolean {
  if (app.archived === true) return false;
  if (isApplicationAbortableProcessing(app)) return true;
  return inUiQueue;
}

export function countAbortableProcessing(
  apps: readonly Pick<Application, 'state' | 'archived'>[],
): number {
  return apps.filter(isApplicationAbortableProcessing).length;
}

export function countExplicitStopEligible(
  apps: readonly Application[],
  inUiQueue: (id: string) => boolean,
): number {
  return apps.filter((app) => isApplicationExplicitStopEligible(app, inUiQueue(app.id))).length;
}

export interface BulkProcessBreakdown {
  firstTime: number;
  billingRetry: number;
  reprocess: number;
  pipelineRetry: number;
  restartProcessing: number;
  blockedUpgrade: number;
}

export function getBulkProcessBreakdown(
  apps: Pick<Application, 'state' | 'lastFailureReason'>[],
  hasProcessingCredits?: boolean,
): BulkProcessBreakdown {
  const breakdown: BulkProcessBreakdown = {
    firstTime: 0,
    billingRetry: 0,
    reprocess: 0,
    pipelineRetry: 0,
    restartProcessing: 0,
    blockedUpgrade: 0,
  };

  for (const app of apps) {
    if (isApplicationAwaitingUpgrade(app, hasProcessingCredits)) {
      breakdown.blockedUpgrade++;
    } else if (isApplicationBillingPipelineError(app)) {
      breakdown.billingRetry++;
    } else if (isApplicationStuckProcessing(app)) {
      breakdown.restartProcessing++;
    } else if (isApplicationReadyToProcess(app, hasProcessingCredits)) {
      breakdown.firstTime++;
    } else if (isApplicationReprocessable(app)) {
      breakdown.reprocess++;
    } else if (isApplicationPipelineErrorRetry(app)) {
      breakdown.pipelineRetry++;
    }
  }

  return breakdown;
}

export function bulkProcessableCount(breakdown: BulkProcessBreakdown): number {
  return (
    breakdown.firstTime
    + breakdown.billingRetry
    + breakdown.reprocess
    + breakdown.pipelineRetry
    + breakdown.restartProcessing
  );
}

/** Bulk toolbar: show Processing when selection includes processable or await-upgrade rows. */
export function getBulkProcessButtonState(
  breakdown: BulkProcessBreakdown,
  hasProcessingCredits: boolean,
): { show: boolean; enabled: boolean; disabledTitleKey: string | null } {
  const processable = bulkProcessableCount(breakdown);
  const show = processable > 0 || breakdown.blockedUpgrade > 0;
  const enabled = hasProcessingCredits && processable > 0;
  let disabledTitleKey: string | null = null;
  if (show && !enabled) {
    disabledTitleKey = processable === 0
      ? 'application.pendingUpgrade.tooltip'
      : 'application.pendingBilling.processBilling';
  }
  return { show, enabled, disabledTitleKey };
}

/** Counts aligned with list badges / status filter (includes display-ready PENDING_UPGRADE). */
export function countDeferredApplicationsForDisplay(
  applications: Application[],
  hasProcessingCredits?: boolean,
): DeferredApplicationCounts {
  let pendingUpgrade = 0;
  let readyToProcess = 0;
  let billingRetry = 0;
  for (const app of applications.filter((a) => a.archived !== true)) {
    if (isApplicationBillingPipelineError(app)) {
      billingRetry++;
    } else if (isApplicationAwaitingUpgrade(app, hasProcessingCredits)) {
      pendingUpgrade++;
    } else if (isApplicationReadyToProcess(app, hasProcessingCredits)) {
      readyToProcess++;
    }
  }
  return { pendingUpgrade, readyToProcess, billingRetry };
}

export function mergeDeferredCounts(
  api: DeferredApplicationCounts,
  fromList: DeferredApplicationCounts,
): DeferredApplicationCounts {
  return {
    pendingUpgrade: Math.max(api.pendingUpgrade, fromList.pendingUpgrade),
    readyToProcess: Math.max(api.readyToProcess, fromList.readyToProcess),
    billingRetry: Math.max(api.billingRetry, fromList.billingRetry),
  };
}

export function reconcileDeferredCountsWithApplications(
  queryClient: QueryClient,
  companyId: string,
  hasProcessingCredits: boolean,
  api: DeferredApplicationCounts,
  options?: { mergeFromList?: boolean },
): DeferredApplicationCounts {
  if (options?.mergeFromList === false) return api;
  const apps = createApplicationListSync(queryClient).getFlattenedListApplications(
    companyId,
    false,
  );
  if (!apps.length) return api;
  return mergeDeferredCounts(api, countDeferredApplicationsForDisplay(apps, hasProcessingCredits));
}

/** @deprecated Prefer countDeferredApplicationsForDisplay */
export function countDeferredApplications(
  applications: Application[],
): DeferredApplicationCounts {
  return countDeferredApplicationsForDisplay(applications, false);
}

/** True when Autumn candidates pool has capacity to resume parked applications. */
export function canProcessPendingApplications(
  status: BillingStatus | null | undefined,
): boolean {
  return hasCandidatesProcessingCredits(status);
}
